import React from 'react';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { ParsedUrlQuery } from 'querystring';
import { cloudflareLoader } from '~/shared/utils/loaders/cloudflareLoader';
import { usePage } from '../../hooks/usePage';
import { getRuntimeConfig } from '~/shared/utils/config';

export const MetaFields = () => {
    const { meta, type, data } = usePage();

    const { title, description, canonical, hideFromRobots, openGraph, url } = meta || {};
    const seoDescription = data?.product?.pageDescription;
    const variantDescription = data?.product?.shortDescription;
    const isPdpPage = type === 'p50ProductDetailsPage';
    //Meta description to render
    const metaDescription = seoDescription || variantDescription || description;

    // if page type is p50ProductDetailsPage then point canonical to its own url.
    const canonicalUrl = isPdpPage ? url : canonical?.url;

    const { query } = useRouter();
    const { config } = getRuntimeConfig();
    const imageWidth = isPdpPage ? 720 : 1200; // PDP: match ProductSchema / cloudflare width (720) so og:image and JSON-LD resolve the same asset URL.
    const imageUrl = openGraph?.image?.src
        ? cloudflareLoader({ src: openGraph?.image.src, width: imageWidth })
        : null;
    const calculatedCanonical =
        config.NEXT_PUBLIC_URL + (canonicalUrl || getDefaultCanonical(query));

    const calculatedUrl = config.NEXT_PUBLIC_URL + (url || getDefaultCanonical(query));

    return (
        <Head>
            {title ? <title key="title">{title}</title> : null}
            {metaDescription ? (
                <meta key="description" name="description" content={metaDescription} />
            ) : null}
            <meta key="metaName" name="name" content={title} />
            {hideFromRobots ? (
                <meta key="hideFromRobots" name="robots" content="noindex, nofollow" />
            ) : null}
            <link key="canonical" rel="canonical" href={calculatedCanonical} />
            <meta key="ogUrl" property="og:url" content={calculatedUrl} />
            <meta property="og:site_name" content="Imerco" />
            {!!imageUrl && !!openGraph?.image && (
                <>
                    <meta key="ogImage" property="og:image" content={imageUrl} />
                    <meta property="og:image:width" content={imageWidth.toString()} />
                    <meta
                        property="og:image:height"
                        content={Math.floor(
                            openGraph.image.height * (imageWidth / openGraph.image.width),
                        ).toString()}
                    />
                </>
            )}
            {!!openGraph?.title && (
                <>
                    <meta key="ogTitle" property="og:title" content={openGraph?.title} />
                </>
            )}
            {!!openGraph?.description && !isPdpPage && (
                <>
                    <meta property="og:description" content={openGraph?.description} />
                </>
            )}
            {!!openGraph?.type && !isPdpPage && (
                <>
                    <meta property="og:type" content={openGraph?.type} />
                </>
            )}

            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:site" content="@Imerco" />
            <meta name="twitter:title" content={openGraph?.title} />
            <meta name="twitter:description" content={openGraph?.description} />
            {!!imageUrl && <meta name="twitter:image" content={imageUrl} />}

            {!!openGraph &&
                Object.entries(openGraph)
                    .filter(
                        (x) => !['image', 'title', 'description', 'type', 'image'].includes(x[0]),
                    )
                    .map(([key, value]) => (
                        <meta key={key} property={`og:${key}`} content={value as string} />
                    ))}

            <meta name="robots" content="max-image-preview:large" />
        </Head>
    );
};

function getDefaultCanonical(query: ParsedUrlQuery) {
    const pathname = Array.isArray(query.slug) ? query.slug.join('/') : query.slug ?? '';
    const params = new URLSearchParams();

    if (query.id) {
        params.append('id', query.id.toString());
    }
    if (query.storeid) {
        params.append('storeid', query.storeid.toString());
    }
    if (query.page && parseInt(query.page.toString(), 10) > 1) {
        params.append('page', query.page.toString());
    }

    const result = `/${pathname?.toLowerCase()}${params.toString() ? `?${params.toString()}` : ''}`;
    return result;
}
